import { countries } from "countries-list"
import {
  COUNTRIES_SANCTIONED_BY_US,
  NOT_AVAILABLE_TO_HIRE_COUNTRIES,
  PRIORITIZED_COUNTRIES,
  GLOBAL_SALARY_CONVERTER_SUPPORTED_COUNTRIES,
} from "./constants"
import type { Countries, Country, CountryCode } from "./types"

/**
 * Version 2.6.1 of the countries-list package uses an outdated country name
 */
const eswatini: Country = {
  code: "SZ",
  name: "Eswatini",
  native: "Eswatini",
  phone: "268",
  continent: "AF",
  capital: "Lobamba",
  currency: "SZL",
  languages: ["en", "ss"],
  emoji: "🇸🇿",
  emojiU: "U+1F1F8 U+1F1FF",
}
const { SZ, ...countriesWithoutSwaziland } = countries
const countriesMap = { ...countriesWithoutSwaziland, SZ: eswatini }

/**
 * All countries are sorted alphabetically by country name.
 */
export const allCountries: Countries = Object.entries(countriesMap)
  .map(([code, country]) => ({
    code: code as CountryCode,
    ...country,
  }))
  .sort((prev, next) => prev.name.localeCompare(next.name))

/**
 * These are the countries that are sanctioned by the US.
 */
export const sanctionedCountries: Countries = COUNTRIES_SANCTIONED_BY_US.map(countryCode => ({
  code: countryCode,
  ...countriesMap[countryCode],
}))

/**
 * These are the countries that are available to hire from a business perspective.
 */
export const hireableCountries: Countries = allCountries.filter(
  country =>
    !NOT_AVAILABLE_TO_HIRE_COUNTRIES.includes(country.code as CountryCode) &&
    !COUNTRIES_SANCTIONED_BY_US.includes(country.code as CountryCode),
)

/**
 * These countries are prioritized in the country list from a business perspective.
 */
export const prioritizedCountries: Countries = PRIORITIZED_COUNTRIES.map(countryCode => ({
  code: countryCode,
  ...countriesMap[countryCode],
}))

/**
 * These are the countries that are available to hire but not prioritized from a business perspective.
 */
export const nonPrioritizedHireableCountries: Countries = hireableCountries.filter(
  country => !PRIORITIZED_COUNTRIES.includes(country.code as CountryCode),
)

/**
 * These are a list of countries without the prioritized countries.
 */
export const nonPrioritizedCountries: Countries = allCountries.filter(
  country => !PRIORITIZED_COUNTRIES.includes(country.code as CountryCode),
)

/**
 * All the countries that are supported under Global Compensation,
 * sorted alphabetically by country name.
 */
export const globalSalaryConverterCountries: Countries =
  GLOBAL_SALARY_CONVERTER_SUPPORTED_COUNTRIES.map(countryCode => ({
    code: countryCode,
    ...countriesMap[countryCode],
  })).sort((prev, next) => prev.name.localeCompare(next.name))

/**
 * Return the detail of a specific country by its code.
 * @param countryCode {CountryCode} The country code to get the country from.
 * @returns {Country}
 */
export const getCountryByCode = (countryCode: CountryCode): Country => ({
  ...countriesMap[countryCode],
  code: countryCode,
})

/**
 * Returns a list of countries specified by the country codes received by argument.
 * @param countryCodes {CountryCode[]} The country codes to get the countries from.
 * @returns {Countries}
 */
export const getCountries = (countryCodes: CountryCode[]): Countries => {
  return countryCodes.map(countryCode => ({ code: countryCode, ...countriesMap[countryCode] }))
}

/**
 * Returns a country ISO code if valid.
 * @param countryCode {string} The country code to verify.
 * @returns {CountryIsoCode | undefined}
 */
export const getCountryIsoCode = (countryCode?: string | null): CountryCode | undefined => {
  if (countryCode && countryCode in countries) {
    return countryCode as CountryCode
  }
  return undefined
}
